import React, { useState, useEffect } from "react";
import {
  FileText,
  Layers,
  CheckSquare,
  Brain,
  ArrowRight,
  Sparkles,
  FileVideo,
  BookText,
} from "lucide-react";
import { ComposerGuy } from "@/components/assistant-ui/thread";
import { IconCanvasLogoSolid } from "@instructure/ui-icons";

const HeroAnimation = () => {
  const [activeOutput, setActiveOutput] = useState(0);
  // const [isConverting, setIsConverting] = useState(false);
  // const [showGhost, setShowGhost] = useState(false);
  const [outputHistory, setOutputHistory] = useState([0]);

  const files = [
    // {
    //   name: "Lecture Slides",
    //   displayName: "lecture_slides.pdf",
    //   color: "bg-amber-400",
    //   type: "PPTX",
    //   icon: FileVideo,
    // },
    // {
    //   name: "Chapter Notes",
    //   displayName: "chapter_notes.pdf",
    //   color: "bg-red-400",
    //   type: "PDF",
    //   icon: FileText,
    // },
    // {
    //   name: "Weekly Reading",
    //   displayName: "weekly_reading.docx",
    //   color: "bg-blue-400",
    //   type: "DOCX",
    //   icon: BookText,
    // },
  ];

  const outputs = [
    {
      name: "Flashcards",
      icon: Layers,
      gradient: "from-emerald-400 to-emerald-600",
      bg: "bg-emerald-400/10",
      border: "border-emerald-300",
      text: "text-emerald-700",
    },
    {
      name: "Practice Quiz",
      icon: CheckSquare,
      gradient: "from-orange-400 to-orange-600",
      bg: "bg-orange-400/10",
      border: "border-orange-300",
      text: "text-orange-700",
    },
    {
      name: "Lecture Summary",
      icon: FileText,
      gradient: "from-blue-400 to-blue-600",
      bg: "bg-blue-400/10",
      border: "border-blue-300",
      text: "text-blue-700",
    },
  ];

  type Phase = "idle" | "converting";

  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase("converting");

      setTimeout(() => {
        setActiveOutput((i) => (i + 1) % outputs.length);
        setOutputHistory((prev) =>
          [(prev[0] + 1) % outputs.length, ...prev].slice(0, 3)
        );
        setPhase("idle");
      }, 500);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  const showGhost = phase === "converting";
  const isConverting = phase === "converting";

  return (
    <div className="w-full flex items-center justify-center hidden md:block">
      <div className="absolute right-20 -top-15 max-w-2xl h-full flex flex-col items-center justify-center">
        {/* Top: Input Files Section - Always visible */}
        <div className="relative mb-8">
          {/* <div className="text-center mb-4">
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              Input
            </span>
          </div> */}

          <div className="relative ml-10 h-20 w-72 flex items-center justify-center">
            {/* Static files */}
            {files.map((file, i) => {
              const spread = 70;
              const rotation = (i - 1) * 12;
              const top = i == 1 ? "20px" : "12px";
              const Icon = file.icon;

              return (
                <div
                  key={i}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: `${30 + i * spread}px`,
                    top: top,
                    transform: `rotate(${rotation}deg)`,
                    zIndex: 3 - i,
                  }}
                >
                  {/* File icon */}
                  <div className="relative">
                    <Icon
                      className="w-16 h-16 text-theme-2"
                      strokeWidth={1.2}
                    />

                    {/* File type badge */}
                    <div
                      className={`absolute -bottom-1 right-1 w-6 h-6 flex items-center justify-center font-semibold rounded-sm shadow-sm bg-red-400 text-white`}
                    >
                      <IconCanvasLogoSolid />
                      {/* {file.type} */}
                    </div>
                  </div>

                  {/* File name */}
                  <div className="mt-1 text-[11px] text-gray-500 font-medium text-center w-20 truncate">
                    {file.name}
                  </div>
                </div>
              );
            })}

            {/* Ghost copies */}
            {/* Ghost files */}
            {showGhost &&
              files.map((file, i) => {
                const spread = 70;
                const rotation = (i - 1) * 12;
                const top = i == 1 ? "20px" : "12px";
                const Icon = file.icon;

                return (
                  <div
                    key={`ghost-${i}`}
                    className="absolute flex flex-col items-center pointer-events-none"
                    style={{
                      left: `${30 + i * spread}px`,
                      top: top,
                      transform: `rotate(${rotation}deg)`,
                      zIndex: 20,
                      animationDelay: `${i * 120}ms`,
                    }}
                  >
                    <div className="relative animate-fade-down">
                      <Icon
                        className="w-16 h-16 text-theme-2"
                        strokeWidth={1.2}
                      />

                      {/* File type badge */}
                      <div
                        className={`absolute bottom-1 right-1 w-6 h-6 flex items-center justify-center font-semibold rounded-sm shadow-sm bg-red-400 text-white`}
                      >
                        <IconCanvasLogoSolid />
                        {/* {file.type} */}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* <div className="text-center mt-2">
            <span className="text-xs font-medium text-gray-500">
              Course Files
            </span>
          </div> */}
        </div>

        {/* Arrow down */}
        {/* <div
          className={`mb-6 ml-10 transition-opacity duration-500 ${
            isConverting ? "opacity-100" : "opacity-30"
          }`}
        >
          <ArrowRight className="w-6 h-6 text-gray-400 rotate-90" />
        </div> */}

        {/* Middle: AI Processing */}
        {/* <div className="relative mb-6">
          <div
            className={`absolute inset-0 -m-6 bg-gradient-to-r ${
              outputs[activeOutput].gradient
            } rounded-full blur-2xl transition-all duration-500 ${
              isConverting ? "opacity-30 scale-125" : "opacity-0 scale-100"
            }`}
          ></div>

          <div
            className={`relative w-20 h-20 bg-gradient-to-br ${
              outputs[activeOutput].gradient
            } rounded-xl flex items-center justify-center shadow-xl transition-all duration-500 ${
              isConverting ? "rotate-180 scale-110" : "rotate-0 scale-100"
            }`}
          >
            <Brain className="w-10 h-10 text-white" />
          </div>

          <div
            className={`absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap transition-all duration-300 ${
              isConverting ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="text-xs font-semibold text-gray-600">
              Generating...
            </span>
          </div>
        </div> */}

        {/* Arrow down */}
        {/* <div className="mb-4 mt-2 transition-opacity duration-500 opacity-100">
          <ArrowRight className="w-6 h-6 text-gray-400 rotate-90" />
        </div> */}

        {/* Bottom: Output Section with Stacked Cards */}
        <div className="relative">
          {/* <div className="text-center mb-4">
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              Output
            </span>
          </div> */}

          <div className="relative w-70 h-30">
            {outputHistory.map((outputIdx, historyIdx) => {
              const output = outputs[outputIdx];
              const OutputIcon = output.icon;
              const isNewest = historyIdx === 0;

              return (
                <div
                  key={`${outputIdx}-${historyIdx}`}
                  className={`absolute left-0 bg-theme-0 rounded-xl shadow-xl border-2 p-4 w-80 transition-all duration-500 ${output.border}`}
                  style={{
                    top: `${historyIdx * 16}px`,
                    zIndex: 10 - historyIdx,
                    opacity: 1 - historyIdx * 0.3,
                    transform: `scale(${1 - historyIdx * 0.05})`,
                  }}
                >
                  {isNewest && <ComposerGuy type="created" />}
                  {/* Header - Only show full for newest */}
                  {isNewest ? (
                    <>
                      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-theme">
                        <div
                          className={`w-10 h-10 bg-gradient-to-br ${output.gradient} rounded-lg flex items-center justify-center shadow-md animate-pop-in`}
                        >
                          <OutputIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-theme-0 text-sm">
                            {output.name}
                          </h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            {/* <Sparkles className="w-3 h-3 text-yellow-500" /> */}
                            <span className="text-xs text-theme-2">
                              Based on your course materials
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Preview content */}
                      <div className="space-y-2">
                        {[1, 2].map((i) => (
                          <div
                            key={i}
                            className={`${output.bg} ${output.border} border rounded-lg p-2 animate-fade-in`}
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                            <div className="flex items-start gap-2">
                              <div
                                className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0`}
                              >
                                <span
                                  className={`text-xs font-bold ${output.text}`}
                                >
                                  {i}
                                </span>
                              </div>
                              <div className="flex-1 space-y-1">
                                <div className="h-1.5 bg-gray-400/20 rounded w-full"></div>
                                <div className="h-1.5 bg-gray-400/20 rounded w-3/5"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    // Condensed view for older cards
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 bg-gradient-to-br ${output.gradient} rounded-lg flex items-center justify-center shadow-md`}
                      >
                        <OutputIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-700 text-sm">
                          {output.name}
                        </h3>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-2">
            <span className="text-xs font-medium text-gray-500">
              Study Materials
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pop-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes fade-down {
          0% {
            opacity: 0.7;
            transform: translate(0, 0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(0, 160px) scale(0.8);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
          opacity: 0;
        }

        .animate-pop-in {
          animation: pop-in 0.5s ease-out forwards;
        }

        .animate-fade-down {
          animation: fade-down 1s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default HeroAnimation;
